import React, { useEffect, useMemo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import {
  ContentTemplate,
  SectionHeader,
  TableActionTypes,
  TABLE_INITIAL_STATE,
  useDataTable,
  MUIDataTable,
  MUIDataTableColumn,
  Typography,
  Button,
  Box,
  Pill,
  configurationTypeData,
  CustomSelect,
  stringToLongDateAndShortTimeWithoutCommaDisplay,
  PillVariant,
} from 'web-library';


import { makeStyles } from 'tss-react/mui';
import { ConfigurationType } from 'web-library/models/DropDown';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { capitalizeFirstLetterOnly } from 'web-library/utils/StringUtils';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import {
  ProfileAmendmentRequestsListingContent,
  usePostProfileAmendmentRequestsListing,
} from '../../../../api-refactored/profile-amendment-requests/usePostProfileAmendmentRequestsListing';
import { useGetProfileAmendmentRequestFilter } from '../../../../api-refactored/profile-amendment-requests/useGetProfileAmendmentRequestFilter';
import RouteKey from '../../../../routes/key';
import { ProfileAmendmentRequestStatus } from '../../../../api-refactored/profile-amendment-requests/usePutCancelProfileAmendmentRequest';
import { useGetUserAuthorities } from '../../../../api-refactored/user/useGetUserAuthorities';

const { admin } = RouteKey.child;
const { profileAmendmentRequests, reviewProfileAmendmentApplication } = RouteKey.admin.child;

export const displayProfileAmendmentRequestPillStatus = (status: string) => {
  const statusToUpperCase = status?.toUpperCase();

  let variant: PillVariant;
  switch (statusToUpperCase) {
    case ProfileAmendmentRequestStatus.Approved:
      variant = 'positive';
      break;
    case ProfileAmendmentRequestStatus.Incomplete:
    case ProfileAmendmentRequestStatus.Rejected:
      variant = 'negative';
      break;
    case ProfileAmendmentRequestStatus.Pending:
      variant = 'info';
      break;
    case ProfileAmendmentRequestStatus.Cancelled:
      variant = 'default';
      break;
    default:
      variant = 'default';
  }
  return <Pill variant={variant} size="M" label={capitalizeFirstLetterOnly(status || '-')} />;
};

const useStyles = makeStyles()((theme) =>
({
  dataTableContainer: {
    marginBottom: theme.spacing(7),
  },
  select: {
    marginBottom: theme.spacing(3),
  },
  updatedByText: {
    color: theme.color.text.textTertiary,
  },
  reviewApplicationLink: {
    padding: '0 !important',
    whiteSpace: 'nowrap',
  },
}));

export const useCheckAdminAccess = () => {
  const history = useHistory();

  const { data: authorities = [], isSuccess: isSuccessAuthorities } = useGetUserAuthorities();

  const hasViewPermission = useMemo(
    () => authorities.includes(AUTHORITY_NAMES.CP_ADMIN_PROFILE_AMENDMENT_REQUESTS_APPROVE),
    [authorities]
  );

  useEffect(() => {
    if (!isSuccessAuthorities) {
      return;
    }
    if (!hasViewPermission) {
      history.push('/error/generic');
    }
  }, [hasViewPermission, history, isSuccessAuthorities]);
};

const tableColumns = ({
  data,
  profileAmendmentRequestFilter,
  classes,
  history,
  t,
}: {
  data: ProfileAmendmentRequestsListingContent[];
  profileAmendmentRequestFilter: ConfigurationType[];
  classes: Record<string,string>;
  history: RouteComponentProps['history'];
  t: TFunction<'translation'>;
}): MUIDataTableColumn[] => [
    {
      name: 'requestDateTime',
      label: t('admin->>ProfileAmendmentRequests.RequestDateTime'),
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex: number) =>
          stringToLongDateAndShortTimeWithoutCommaDisplay(data[dataIndex].requestDateTime),
      },
    },
    {
      name: 'requestorName',
      label: t('admin->>ProfileAmendmentRequests.RequestorName'),
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex: number) => data[dataIndex].requestorName,
      },
    },
    {
      name: 'nricFinPassportNo',
      label: t('admin->>ProfileAmendmentRequests.NricFinPassportNo'),
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Typography variant="body2">{data[dataIndex].requestorNricPassport}</Typography>
        ),
      },
    },
    {
      name: 'dateTimeUpdated',
      label: t('admin->>ProfileAmendmentRequests.DateTimeUpdated'),
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <>
            {data[dataIndex].updatedAt
              ? stringToLongDateAndShortTimeWithoutCommaDisplay(data[dataIndex].updatedAt)
              : '-'}
            {data[dataIndex].updatedBy && data[dataIndex].updatedBy !== '-' && (
              <Typography className={classes.updatedByText} variant="subtitle2">
                {t('admin->>ProfileAmendmentRequests.UpdatedBy', {
                  adminName: data[dataIndex].updatedBy,
                })}
              </Typography>
            )}
          </>
        ),
      },
    },
    {
      name: 'status',
      label: t('admin->>ProfileAmendmentRequests.Status'),
      options: {
        filter: true,
        filterType: 'custom',
        filterOptions: {
          fullWidth: true,
          display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
            <CustomSelect
              className={classes.select}
              label={t('admin->>ProfileAmendmentRequests.Status')}
              placeholder={t('admin->>ProfileAmendmentRequests.SelectStatus')}
              displayEmpty
              onChange={(event) => {
                // eslint-disable-next-line no-param-reassign
                filterList[index][0] = event.target.value as string;
                onChange(filterList[index], index, column);
              }}
              menuItems={configurationTypeData(profileAmendmentRequestFilter)}
              value={filterList[index][0]}
              variant="filled"
            />
          ),
        },
        customBodyRenderLite: (dataIndex: number) => (
          <>{displayProfileAmendmentRequestPillStatus(data[dataIndex].status)}</>
        ),
      },
    },
    {
      name: 'actions',
      label: t('admin->>ProfileAmendmentRequests.Actions'),
      options: {
        filter: false,
        setCellProps: () => ({ style: { paddingTop: '5px' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <Button
            className={classes.reviewApplicationLink}
            onClick={() =>
              history.push(
                `/${admin}/${profileAmendmentRequests.route}/${data[dataIndex].uuid}/${reviewProfileAmendmentApplication.route}`
              )
            }
            variant="text"
            color="primary"
          >
            {t('admin->>ProfileAmendmentRequests.ReviewApplication')}
          </Button>
        ),
      },
    },
  ];

const ProfileAmendmentRequestsDataTable = () => {
  const { classes } = useStyles();
  const history = useHistory();
  const { t } = useTranslation();

  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    size: 5,
    page: 0,
    search: '',
    filters: {},
  });

  useCheckAdminAccess();

  const { data: profileAmendmentRequestFilter } = useGetProfileAmendmentRequestFilter();

  const {
    mutate: mutatePostProfileAmendmentRequestsListing,
    isLoading: isLoadingProfileAmendmentRequestsListing,
  } = usePostProfileAmendmentRequestsListing({
    onSuccess: (data) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
  });

  useEffect(() => {
    mutatePostProfileAmendmentRequestsListing({
      page: state.page,
      pageSize: state.size,
      status: state.filters.status?.[0] || null,
    });
  }, [
    mutatePostProfileAmendmentRequestsListing,
    state.filters.status,
    state.order,
    state.page,
    state.size,
    state.sortColumn,
  ]);

  return (
    <ContentTemplate type="full">
      <Box className={classes.dataTableContainer}>
        <SectionHeader title={t('admin->>ProfileAmendmentRequests.ApplicationList')} />
        <MUIDataTable
          isLoading={isLoadingProfileAmendmentRequestsListing}
          state={state}
          dispatch={tableDispatch}
          columns={tableColumns({
            classes,
            data: state.data,
            profileAmendmentRequestFilter: profileAmendmentRequestFilter?.status as ConfigurationType[],
            history,
            t,
          })}
          data={state.data}
          options={{
            ...tOptions,
            pagination: true,
            filter: true,
          }}
        />
      </Box>
    </ContentTemplate>
  );
};

export default ProfileAmendmentRequestsDataTable;
